import {
  BufferGeometry,
  DoubleSide,
  Float32BufferAttribute,
  Group,
  LineBasicMaterial,
  LineLoop,
  Mesh,
  MeshBasicMaterial,
  OctahedronGeometry,
  Vector3,
} from 'three';
import type { Surface } from '../world/Surface';

/** One drone's authored patrol loop, ready to draw. */
export interface DroneRouteSpec {
  points: Array<[number, number, number]>;
  /** The surface the drone hangs from; its normal lifts the drawing off it. */
  surface: Surface | null;
  magnetic: boolean;
}

const STANDARD_COLOR = 0x63f0d0;
const MAGNETIC_COLOR = 0x9ff6ff;
/** How far the drawing floats off the surface, metres. */
const SURFACE_LIFT = 0.06;

/**
 * The drawn patrol route of every drone in the room builder: a loop line, a
 * marker on each waypoint, and a translucent patch showing the area swept.
 *
 * It is a builder overlay, not a gameplay object: the runtime drones do not
 * know it exists. Everything is rebuilt from the map document, so an edit is
 * shown by calling `setRoutes` again.
 */
export class DroneRouteView {
  readonly group = new Group();

  private readonly standardLine = new LineBasicMaterial({
    color: STANDARD_COLOR,
    transparent: true,
    opacity: 0.85,
  });
  private readonly magneticLine = new LineBasicMaterial({
    color: MAGNETIC_COLOR,
    transparent: true,
    opacity: 0.85,
  });
  private readonly standardMarker = new MeshBasicMaterial({ color: STANDARD_COLOR });
  private readonly magneticMarker = new MeshBasicMaterial({ color: MAGNETIC_COLOR });
  private readonly standardArea = new MeshBasicMaterial({
    color: STANDARD_COLOR,
    transparent: true,
    opacity: 0.1,
    side: DoubleSide,
    depthWrite: false,
  });
  private readonly magneticArea = new MeshBasicMaterial({
    color: MAGNETIC_COLOR,
    transparent: true,
    opacity: 0.1,
    side: DoubleSide,
    depthWrite: false,
  });
  private readonly markerGeometry = new OctahedronGeometry(0.13);

  constructor() {
    this.group.visible = false;
  }

  /** Redraw the routes. An empty list hides the overlay. */
  setRoutes(routes: readonly DroneRouteSpec[]): void {
    this.clear();
    let drawn = 0;
    for (const route of routes) {
      if (route.points.length < 2) continue;
      const lift = route.surface ? route.surface.normal.clone().multiplyScalar(SURFACE_LIFT) : null;
      const points = route.points.map(([x, y, z]) => {
        const point = new Vector3(x, y, z);
        if (lift) point.add(lift);
        return point;
      });
      this.group.add(
        new LineLoop(
          new BufferGeometry().setFromPoints(points),
          route.magnetic ? this.magneticLine : this.standardLine,
        ),
      );
      this.group.add(this.buildArea(points, route.magnetic));
      for (const point of points) {
        const marker = new Mesh(
          this.markerGeometry,
          route.magnetic ? this.magneticMarker : this.standardMarker,
        );
        marker.position.copy(point);
        this.group.add(marker);
      }
      drawn += 1;
    }
    this.group.visible = drawn > 0;
  }

  /** A fan of triangles from the loop's centroid, so the swept area reads. */
  private buildArea(points: readonly Vector3[], magnetic: boolean): Mesh {
    const centroid = new Vector3();
    for (const point of points) centroid.add(point);
    centroid.divideScalar(points.length);

    const vertices: number[] = [];
    for (let index = 0; index < points.length; index += 1) {
      const current = points[index]!;
      const next = points[(index + 1) % points.length]!;
      vertices.push(
        centroid.x,
        centroid.y,
        centroid.z,
        current.x,
        current.y,
        current.z,
        next.x,
        next.y,
        next.z,
      );
    }
    const geometry = new BufferGeometry();
    geometry.setAttribute('position', new Float32BufferAttribute(vertices, 3));
    geometry.computeVertexNormals();
    return new Mesh(geometry, magnetic ? this.magneticArea : this.standardArea);
  }

  /** Remove everything currently drawn. */
  clear(): void {
    for (const child of [...this.group.children]) {
      this.group.remove(child);
      // The markers share one geometry, which must outlive a redraw; the loop
      // and area geometries are rebuilt each time and are safe to release.
      const mesh = child as Mesh & { geometry?: { dispose(): void } };
      if (mesh.geometry && mesh.geometry !== this.markerGeometry) mesh.geometry.dispose();
    }
    this.group.visible = false;
  }

  dispose(): void {
    this.clear();
    this.markerGeometry.dispose();
    this.standardLine.dispose();
    this.magneticLine.dispose();
    this.standardMarker.dispose();
    this.magneticMarker.dispose();
    this.standardArea.dispose();
    this.magneticArea.dispose();
  }
}
